import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Input } from "~/react/ui/input"
import { Label } from "~/react/ui/label"
import { Hint } from "~/react/ui/hint"

type DomainOwner = {
  first_name: string
  last_name: string
  email: string
  company: string
  address: string
  postal_code: string
  city: string
  country: string
}

type DomainDetailsTabProps = {
  redirectUrl: string
  redirectUrlLogo: string | null
  isRedirectUrlInvalid: boolean
  owner: DomainOwner
  onRedirectUrlChange: (value: string) => void
  onRedirectUrlBlur: () => void
  onRedirectUrlClear: () => void
  onOwnerFieldChange: (field: keyof DomainOwner, value: string) => void
  i18n: typeof i18n
}

export default function DomainDetailsTab({
  redirectUrl,
  redirectUrlLogo,
  isRedirectUrlInvalid,
  owner,
  onRedirectUrlChange,
  onRedirectUrlBlur,
  onRedirectUrlClear,
  onOwnerFieldChange,
  i18n,
}: DomainDetailsTabProps) {
  return (
    <>
      <section className="mb-8">
        <h4 className="text-md-medium mb-1">{i18n.t("js.email_accounts.provisioning.domain_redirection_title")}</h4>
        <Label htmlFor="domain-redirect-url">{i18n.t("js.email_accounts.provisioning.redirect_url_label")}</Label>
        <div className="domain-field">
          {redirectUrlLogo && (
            <img
              className="domain-field__logo"
              src={redirectUrlLogo}
              alt=""
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.style.display = "none"
              }}
            />
          )}
          <Input
            type="url"
            id="domain-redirect-url"
            placeholder={i18n.t("js.email_accounts.provisioning.redirect_url_placeholder")}
            value={redirectUrl}
            onChange={(e) => {
              onRedirectUrlChange(e.target.value)
            }}
            onBlur={onRedirectUrlBlur}
            aria-invalid={isRedirectUrlInvalid}
            className="domain-field__input"
          />
          {redirectUrl.length > 0 && (
            <Button
              variant="ghost"
              iconOnly
              size="xs"
              aria-label={i18n.t("js.email_accounts.provisioning.redirect_url_clear")}
              onClick={() => {
                onRedirectUrlClear()
              }}
            >
              <Icon icon="times" size="sm" aria-hidden="true" />
            </Button>
          )}
        </div>
        {isRedirectUrlInvalid && (
          <Hint variant="error" className="mt-2">
            {i18n.t("js.email_accounts.provisioning.redirect_url_invalid")}
          </Hint>
        )}
      </section>

      <h4 className="text-md-medium mb-1">{i18n.t("js.email_accounts_center.domain.details.owner_title")}</h4>
      <p className="text-2xs text-body-secondary mb-4">
        {i18n.t("js.email_accounts_center.domain.details.owner_description")}
      </p>

      <div className="row g-4 mb-4">
        <div className="col-6">
          <Label htmlFor="domain-owner-first-name">
            {i18n.t("js.email_accounts_center.domain.details.owner.first_name")}
          </Label>
          <Input
            id="domain-owner-first-name"
            name="domain-owner-first-name"
            fullWidth
            value={owner.first_name}
            onChange={(e) => onOwnerFieldChange("first_name", e.target.value)}
          />
        </div>
        <div className="col-6">
          <Label htmlFor="domain-owner-last-name">
            {i18n.t("js.email_accounts_center.domain.details.owner.last_name")}
          </Label>
          <Input
            id="domain-owner-last-name"
            name="domain-owner-last-name"
            fullWidth
            value={owner.last_name}
            onChange={(e) => onOwnerFieldChange("last_name", e.target.value)}
          />
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-6">
          <Label htmlFor="domain-owner-email">{i18n.t("js.email_accounts_center.domain.details.owner.email")}</Label>
          <Input
            id="domain-owner-email"
            name="domain-owner-email"
            fullWidth
            value={owner.email}
            onChange={(e) => onOwnerFieldChange("email", e.target.value)}
          />
        </div>
        <div className="col-6">
          <Label htmlFor="domain-owner-company">
            {i18n.t("js.email_accounts_center.domain.details.owner.company")}
          </Label>
          <Input
            id="domain-owner-company"
            name="domain-owner-company"
            fullWidth
            value={owner.company}
            onChange={(e) => onOwnerFieldChange("company", e.target.value)}
          />
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-8">
          <Label htmlFor="domain-owner-address">
            {i18n.t("js.email_accounts_center.domain.details.owner.address")}
          </Label>
          <Input
            id="domain-owner-address"
            name="domain-owner-address"
            fullWidth
            value={owner.address}
            onChange={(e) => onOwnerFieldChange("address", e.target.value)}
          />
        </div>
        <div className="col-4">
          <Label htmlFor="domain-owner-postal-code">
            {i18n.t("js.email_accounts_center.domain.details.owner.postal_code")}
          </Label>
          <Input
            id="domain-owner-postal-code"
            name="domain-owner-postal-code"
            fullWidth
            value={owner.postal_code}
            onChange={(e) => onOwnerFieldChange("postal_code", e.target.value)}
          />
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-6">
          <Label htmlFor="domain-owner-city">{i18n.t("js.email_accounts_center.domain.details.owner.city")}</Label>
          <Input
            id="domain-owner-city"
            name="domain-owner-city"
            fullWidth
            value={owner.city}
            onChange={(e) => onOwnerFieldChange("city", e.target.value)}
          />
        </div>
        <div className="col-6">
          <Label htmlFor="domain-owner-country">
            {i18n.t("js.email_accounts_center.domain.details.owner.country")}
          </Label>
          <Input
            id="domain-owner-country"
            name="domain-owner-country"
            fullWidth
            value={owner.country}
            onChange={(e) => onOwnerFieldChange("country", e.target.value)}
          />
        </div>
      </div>
    </>
  )
}
